import type { ReactNode } from 'react'
import type {
  AgendaItemVM, AutomationVM, CheckpointVM, HandoffMessageVM, HandoffRuleVM, MessageVM, SignalVM,
} from '@/utils/workflowOverview'
import type { OrderWorkflowDefinitionJson } from '@/types/workflowDefinition'
import { AddSlotForm } from './configure/AddSlotForm'
import { AddAutomationForm } from './configure/AddAutomationForm'
import { AddHandoffRuleForm } from './configure/AddHandoffRuleForm'
import { AskItemEditor, ParkMessageEditor, TemplateEditor } from './configure/editors'
import { EntryControls, InlineFindings, OriginBadge } from './configure/entryControls'
import { HandoffMessageEditor, HandoffRuleControls, HandoffRuleEditor } from './configure/handoffEditors'
import { AddSignalForm, SignalControls, SignalInstructionEditor } from './configure/signalEditors'
import type { ConfigHandoffRuleVM, ConfigSignalVM } from '@/utils/workflowConfigView'
import { CollapsibleSection, Badge } from '@/components/ui'
import { TemplateText, TokenChip } from './TokenChip'
import { humanize, capabilityLabel } from '@/utils/friendlyLabels'
import { useWorkflowConfigStore } from '@/stores/workflowConfigStore'

type ConfigFlags = { origin: 'base' | 'added'; disabled: boolean }

const cnItemDim = (d?: boolean) => (d ? 'min-w-0 space-y-1 opacity-50' : 'min-w-0 space-y-1')

export function SectionCard({ title, subtitle, children }: {
  title: string; subtitle?: string; children: ReactNode
}) {
  return (
    <section className="rounded-2xl border border-border bg-card shadow-xs p-4 space-y-3">
      <div>
        <h2 className="text-sm font-semibold text-foreground">{title}</h2>
        {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
      </div>
      {children}
    </section>
  )
}

export function BilingualText({ text, slotLabels, segments = false }: {
  text: Record<string, string>
  slotLabels?: Record<string, string>
  /** SA-308: chip `[[ … ]]` optional segments — only where the engine honors them. */
  segments?: boolean
}) {
  const entries = Object.entries(text)
  if (entries.length === 0) return null
  return (
    <div className="grid gap-1 sm:grid-cols-2">
      {entries.map(([lang, value]) => (
        <p key={lang} className="text-xs text-foreground">
          <span className="font-mono uppercase text-muted-foreground mr-1">{lang}</span>
          <TemplateText text={value} slotLabels={slotLabels} segments={segments} />
        </p>
      ))}
    </div>
  )
}

function KeyHeadline({ keyName }: { keyName: string }) {
  return (
    <p className="text-xs">
      <span className="font-medium text-foreground">{humanize(keyName)}</span>
      <span className="ml-1.5 font-mono text-[10px] text-muted-foreground">{keyName}</span>
    </p>
  )
}

const REQUIREDNESS_LABEL: Record<AgendaItemVM['requiredness'], string> = {
  always: 'Always asked',
  conditional: 'Sometimes asked',
  optional: 'Optional',
}

export function AgendaSection({ personaName, agenda, editable = false, base = null, slotLabels }: {
  personaName: string | null
  agenda: (AgendaItemVM & Partial<ConfigFlags>)[]
  editable?: boolean
  base?: OrderWorkflowDefinitionJson | null
  slotLabels?: Record<string, string>
}) {
  return (
    <CollapsibleSection
      title={`What ${personaName ?? 'the assistant'} collects, in order`}
      subtitle="Sorted exactly as the AI prioritizes them in conversation."
      summary={`${agenda.length} ${agenda.length === 1 ? 'thing' : 'things'} collected, in order`}
      storageKey="wf-section:agenda"
      variant="soft"
      icon="📋"
    >
      <ol className="space-y-3">
        {agenda.map((item, index) => (
          <li key={item.key} className="flex gap-3">
            <span className="shrink-0 w-6 h-6 rounded-full bg-muted text-xs font-medium text-muted-foreground inline-flex items-center justify-center">
              {index + 1}
            </span>
            <div className={cnItemDim(item.disabled)}>
              <p className="text-sm text-foreground">
                <span className="font-medium">{item.label}</span>
                <span className="ml-2 text-xs text-muted-foreground">
                  {REQUIREDNESS_LABEL[item.requiredness]}
                </span>
                <span className="ml-2 inline-flex gap-1 items-center">
                  <OriginBadge origin={item.origin} disabled={item.disabled} />
                  {editable && item.origin && (
                    <EntryControls
                      section={item.itemKind === 'need' ? 'needs' : 'slots'}
                      itemKey={item.key}
                      origin={item.origin}
                      disabled={item.disabled ?? false}
                    />
                  )}
                </span>
              </p>
              {item.condition && (
                <p className="text-xs text-muted-foreground">{item.condition}</p>
              )}
              {item.options && (
                <p className="text-xs text-muted-foreground">
                  Choices: {item.options.join(' / ')}
                </p>
              )}
              {editable && !item.disabled ? (
                <AskItemEditor item={item} languages={base?.languages} />
              ) : !item.disabled ? (
                <BilingualText text={item.asks} slotLabels={slotLabels} />
              ) : null}
              {editable && (
                <InlineFindings
                  paths={[
                    `slots.${item.key}`,
                    `needs.${item.key}`,
                    `conversation.asks.${item.key}`,
                  ]}
                />
              )}
              {item.promptHint && (
                <details className="text-xs text-muted-foreground">
                  <summary className="cursor-pointer">Exact instructions to the AI</summary>
                  <p className="mt-1">{item.promptHint}</p>
                </details>
              )}
            </div>
          </li>
        ))}
      </ol>
      {editable && base && <AddSlotForm base={base} />}
    </CollapsibleSection>
  )
}

export function AutomationsSection({ automations, editable = false, base = null, slotLabels }: {
  automations: (AutomationVM & Partial<ConfigFlags>)[]
  editable?: boolean
  base?: OrderWorkflowDefinitionJson | null
  slotLabels?: Record<string, string>
}) {
  return (
    <CollapsibleSection
      title="What happens automatically"
      subtitle="Steps the system performs on its own — no one has to remember them."
      summary={`${automations.length} automatic ${automations.length === 1 ? 'step' : 'steps'}`}
      storageKey="wf-section:automations"
      variant="soft"
      icon="⚙️"
    >
      <ul className="space-y-2">
        {automations.map((a) => {
          const section = a.category === 'derived' ? 'derived' : 'side_effects'
          return (
            <li key={a.key} className={a.disabled ? 'text-sm opacity-50' : 'text-sm text-foreground'}>
              <span className="inline-flex gap-2 items-center flex-wrap">
                <OriginBadge origin={a.origin} disabled={a.disabled} />
                {editable && a.origin && (
                  <EntryControls section={section} itemKey={a.key}
                    origin={a.origin} disabled={a.disabled ?? false} />
                )}
                <span>
                  {a.dependsOn.length > 0 && (
                    <>Once we have{' '}
                      {a.dependsOn.map((dep, i) => (
                        <span key={dep}>{i > 0 && ', '}<TokenChip label={dep} /></span>
                      ))}{' '}→{' '}
                    </>
                  )}
                  runs <span className="font-medium">{capabilityLabel(a.capability)}</span>{' '}
                  <span className="font-mono text-[10px] text-muted-foreground">{a.capability}</span>
                  {a.when && <span className="text-muted-foreground"> ({a.when})</span>}
                  {a.writes && <> → fills <TokenChip label={slotLabels?.[a.writes] ?? humanize(a.writes)} title={a.writes} /></>}
                </span>
              </span>
              {a.compensate && (
                <p className="text-xs text-muted-foreground mt-0.5">
                  If this later needs undoing, the system runs{' '}
                  <span className="font-medium">{capabilityLabel(a.compensate)}</span>{' '}
                  <span className="font-mono text-[10px] text-muted-foreground">{a.compensate}</span> automatically.
                </p>
              )}
              {editable && (
                <InlineFindings paths={[`derived.${a.key}`, `side_effects.${a.key}`]} />
              )}
            </li>
          )
        })}
      </ul>
      {editable && base && <AddAutomationForm base={base} />}
    </CollapsibleSection>
  )
}

export function CheckpointsSection({ checkpoints, slotLabels, editable = false }: {
  checkpoints: (CheckpointVM & { addable?: { key: string; label: string }[] })[]
  slotLabels?: Record<string, string>
  editable?: boolean
}) {
  const removeRequirement = useWorkflowConfigStore((s) => s.removeCheckpointRequirement)
  const addRequirement = useWorkflowConfigStore((s) => s.addCheckpointRequirement)
  const advanced = useWorkflowConfigStore((s) => s.advanced)
  const canEdit = editable && advanced
  return (
    <CollapsibleSection
      title="The confirmation gate"
      subtitle="Nothing is charged or booked until the customer confirms this summary."
      summary="Confirmation summary shown before anything is charged"
      storageKey="wf-section:checkpoints"
      variant="soft"
      icon="✅"
    >
      {checkpoints.map((cp) => (
        <div key={cp.key} className="space-y-1">
          <p className="text-xs text-muted-foreground">
            Shown once we know:{' '}
            {cp.requires.map((req, i) => (
              <span key={cp.requireKeys[i]}>
                {i > 0 && ', '}
                <TokenChip label={req} />
                {canEdit && (
                  <button
                    type="button"
                    aria-label={`remove ${cp.requireKeys[i]} from ${cp.key}`}
                    className="ml-0.5 text-muted-foreground hover:text-destructive"
                    onClick={() => removeRequirement(cp.key, cp.requireKeys[i])}
                  >
                    ×
                  </button>
                )}
              </span>
            ))}
          </p>
          {canEdit && (cp.addable?.length ?? 0) > 0 && (
            <select
              aria-label={`add to ${cp.key}`}
              className="rounded-sm border border-border bg-background px-2 py-0.5 text-xs text-foreground"
              value=""
              onChange={(e) => {
                if (e.target.value) addRequirement(cp.key, e.target.value)
              }}
            >
              <option value="">+ Require another…</option>
              {(cp.addable ?? []).map((a) => (
                <option key={a.key} value={a.key}>
                  {a.label}
                </option>
              ))}
            </select>
          )}
          <BilingualText text={cp.text} slotLabels={slotLabels} segments />
        </div>
      ))}
    </CollapsibleSection>
  )
}

export function MessagesSection({ messages, editable = false, base = null, slotLabels }: {
  messages: MessageVM[]
  editable?: boolean
  base?: OrderWorkflowDefinitionJson | null
  slotLabels?: Record<string, string>
}) {
  return (
    <CollapsibleSection
      title="Every word customers can see"
      subtitle="Verbatim message templates — nothing else is scripted."
      summary={`${messages.length} customer-visible ${messages.length === 1 ? 'message' : 'messages'}`}
      storageKey="wf-section:messages"
      variant="soft"
      icon="💬"
    >
      <ul className="space-y-3">
        {messages.map((m) => (
          <li key={m.key} className="space-y-0.5">
            <KeyHeadline keyName={m.key} />
            {editable && base && m.group === 'template' ? (
              <TemplateEditor templateKey={m.key} base={base} />
            ) : editable && base && m.group === 'park' ? (
              <ParkMessageEditor base={base} />
            ) : (
              <>
                <BilingualText text={m.text} slotLabels={slotLabels} segments={m.group === 'template'} />
                {editable && m.group === 'handoff' && (
                  <p className="text-xs text-muted-foreground">
                    Edited on the Handoff tab.
                  </p>
                )}
              </>
            )}
          </li>
        ))}
      </ul>
    </CollapsibleSection>
  )
}

const MODE_WORDING: Record<'hard' | 'advisory' | 'notify', string> = {
  hard: 'hands off immediately',
  advisory: 'suggests offering a handoff',
  notify: 'notifies the team and keeps helping',
}

export function HandoffSection({
  personaName,
  handoff,
  editable = false,
  base = null,
  workflowNames,
  slotLabels,
}: {
  personaName: string | null
  handoff: { rules: (HandoffRuleVM & Partial<ConfigFlags>)[]; messages: HandoffMessageVM[] }
  editable?: boolean
  base?: OrderWorkflowDefinitionJson | null
  workflowNames?: Record<string, string>
  slotLabels?: Record<string, string>
}) {
  return (
    <div className="space-y-4">
      <CollapsibleSection
        title={`When ${personaName ?? 'the assistant'} brings in a human`}
        subtitle="Deterministic handoff rules — evaluated on every turn, ahead of the AI's own judgment."
        summary={`${handoff.rules.length} handoff ${handoff.rules.length === 1 ? 'rule' : 'rules'}`}
        storageKey="wf-section:handoff"
        variant="soft"
        icon="🤝"
      >
        {handoff.rules.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No handoff rules yet — add one in Customize mode.
          </p>
        ) : (
          <ul className="space-y-3">
            {handoff.rules.map((rule, index) => {
              const off = rule.disabled ?? !rule.enabled
              return (
                <li key={rule.id ?? `rule-${index}`} className={off ? 'space-y-1 opacity-50' : 'space-y-1'}>
                  <p className="text-sm text-foreground">
                    <span className="font-medium">{rule.kindLabel}</span>
                    <span className="ml-2 text-xs text-muted-foreground">
                      {MODE_WORDING[rule.mode]}
                    </span>
                    <span className="ml-2 inline-flex gap-1 items-center">
                      {rule.action === 'switch' && rule.switchTarget && (
                        <Badge>
                          Switches to: {workflowNames?.[rule.switchTarget] ?? rule.switchTarget}
                          <span className="ml-1 font-normal text-primary-foreground/80">
                            (falls back to handoff)
                          </span>
                        </Badge>
                      )}
                      <OriginBadge origin={rule.origin} disabled={off} />
                      {editable && rule.id && rule.origin && (
                        <HandoffRuleControls rule={rule as ConfigHandoffRuleVM} />
                      )}
                    </span>
                  </p>
                  <p className="text-xs text-muted-foreground">{rule.description}</p>
                  {rule.keywords && !(editable && !off && rule.id) && (
                    <div className="space-y-0.5">
                      {Object.entries(rule.keywords).map(([lang, phrases]) => (
                        <p key={lang} className="text-xs text-foreground">
                          <span className="font-mono uppercase text-muted-foreground mr-1">{lang}</span>
                          {phrases.join(' · ')}
                        </p>
                      ))}
                    </div>
                  )}
                  {editable && !off && rule.id && (
                    <HandoffRuleEditor
                      rule={rule as ConfigHandoffRuleVM}
                      languages={base?.languages ?? []}
                    />
                  )}
                </li>
              )
            })}
          </ul>
        )}
        {editable && base && <AddHandoffRuleForm base={base} />}
      </CollapsibleSection>

      <CollapsibleSection
        title="What customers hear at handoff"
        subtitle="Verbatim messages sent the moment a rule matches."
        summary={`${handoff.messages.length} handoff ${handoff.messages.length === 1 ? 'message' : 'messages'}`}
        storageKey="wf-section:handoff-messages"
        variant="soft"
        icon="📣"
      >
        <ul className="space-y-3">
          {handoff.messages.map((m) => (
            <li key={m.key} className="space-y-0.5">
              <KeyHeadline keyName={m.key} />
              {editable && base ? (
                <HandoffMessageEditor msgKey={m.key} base={base} />
              ) : (
                <BilingualText text={m.text} slotLabels={slotLabels} />
              )}
            </li>
          ))}
        </ul>
      </CollapsibleSection>
    </div>
  )
}

const SIGNAL_EVENT_LABELS: Record<string, string> = {
  payment_received: 'Payment received',
  completion: 'Order complete',
}

export function SignalsSection({
  personaName,
  signals,
  editable = false,
  base = null,
  slotLabels,
}: {
  personaName: string | null
  signals: (SignalVM & Partial<ConfigFlags>)[]
  editable?: boolean
  base?: OrderWorkflowDefinitionJson | null
  slotLabels?: Record<string, string>
}) {
  return (
    <CollapsibleSection
      title={`Behind-the-scenes cues ${personaName ?? 'the assistant'} follows`}
      subtitle="Private instructions woven into the reply when a lifecycle moment fires — customers never see them verbatim."
      summary={`${signals.length} ${signals.length === 1 ? 'signal' : 'signals'}`}
      storageKey="wf-section:signals"
      variant="soft"
      icon="✨"
    >
      {signals.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No signals yet — add one in Customize mode.
        </p>
      ) : (
        <ul className="space-y-3">
          {signals.map((signal, index) => {
            const off = signal.disabled ?? !signal.enabled
            return (
              <li key={signal.id ?? `signal-${index}`} className={off ? 'space-y-1 opacity-50' : 'space-y-1'}>
                <p className="text-sm text-foreground">
                  <span className="font-medium">{SIGNAL_EVENT_LABELS[signal.on] ?? signal.on}</span>
                  <span className="ml-2 text-xs text-muted-foreground">
                    fires once, on this event
                  </span>
                  <span className="ml-2 inline-flex gap-1 items-center">
                    <OriginBadge origin={signal.origin} disabled={off} />
                    {editable && signal.id && signal.origin && (
                      <SignalControls signal={signal as ConfigSignalVM} />
                    )}
                  </span>
                </p>
                {editable && !off && signal.id && base ? (
                  <SignalInstructionEditor signal={signal as ConfigSignalVM} base={base} />
                ) : (
                  <BilingualText text={signal.instruction} slotLabels={slotLabels} />
                )}
              </li>
            )
          })}
        </ul>
      )}
      {editable && base && <AddSignalForm base={base} />}
    </CollapsibleSection>
  )
}
